import type { Tour } from '@/types'
import { differenceInCalendarDays, parseISO } from 'date-fns'
import { cn } from '@/lib/utils'

type Phase = 'upcoming' | 'in-progress' | 'completed'

export interface TourStatusInfo {
  phase: Phase
  totalDays: number
  dayNumber: number
  daysUntil: number
  pct: number
}

export function getTourStatus(tour: Tour, now: Date = new Date()): TourStatusInfo {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const start = parseISO(tour.fecha_inicio)
  const end = parseISO(tour.fecha_fin)
  const totalDays = Math.max(1, differenceInCalendarDays(end, start) + 1)

  if (today < start) {
    const daysUntil = differenceInCalendarDays(start, today)
    return { phase: 'upcoming', totalDays, dayNumber: 0, daysUntil, pct: 0 }
  }
  if (today > end) {
    return { phase: 'completed', totalDays, dayNumber: totalDays, daysUntil: 0, pct: 100 }
  }
  const dayNumber = differenceInCalendarDays(today, start) + 1
  const pct = Math.round((dayNumber / totalDays) * 100)
  return { phase: 'in-progress', totalDays, dayNumber, daysUntil: 0, pct }
}

// Compact chip — sits next to the tour name.
export function TourStatusChip({ tour, className }: { tour: Tour; className?: string }) {
  const s = getTourStatus(tour)
  const label =
    s.phase === 'upcoming'
      ? `in ${s.daysUntil}d`
      : s.phase === 'completed'
      ? 'done'
      : `${s.dayNumber}/${s.totalDays}`
  const tone =
    s.phase === 'upcoming'
      ? 'bg-muted text-muted-foreground'
      : s.phase === 'completed'
      ? 'bg-muted text-muted-foreground'
      : 'bg-primary/15 text-primary'

  return (
    <span
      className={cn(
        'shrink-0 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold tabular-nums tracking-tight normal-case',
        tone,
        className,
      )}
      title={
        s.phase === 'upcoming'
          ? `Starts in ${s.daysUntil} day${s.daysUntil === 1 ? '' : 's'}`
          : s.phase === 'completed'
          ? 'Tour completed'
          : `Day ${s.dayNumber} of ${s.totalDays} · ${s.pct}%`
      }
    >
      {label}
    </span>
  )
}

// Progress bar with label — for manager schedule header.
export function TourStatusBar({ tour, className }: { tour: Tour; className?: string }) {
  const s = getTourStatus(tour)
  const label =
    s.phase === 'upcoming'
      ? `Starts in ${s.daysUntil} day${s.daysUntil === 1 ? '' : 's'}`
      : s.phase === 'completed'
      ? 'Tour completed'
      : `Day ${s.dayNumber} of ${s.totalDays}`

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground tabular-nums">{s.pct}%</span>
      </div>
      <div className="h-1 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-primary transition-[width] dur-slow ease-out-soft"
          style={{ width: `${s.pct}%` }}
        />
      </div>
    </div>
  )
}
